import { useCallback, useMemo } from 'react';

import { attributeColumnKey, decodeColumnKeys, encodeColumnInput, standardColumnKey } from './internal/columnKeys';
import { moveWithinReorderableSlots } from './internal/columnLayout';
import type { DataTableAttributeColumns, DataTableColumn, DataTableColumnRef, DataTableColumnRefInput } from './types';
import { useControllableState } from './useControllableState';

export type ResolvedColumn<T> = DataTableColumn<T> & {
  ref: DataTableColumnRef;
  isAttribute: boolean;
};

export type UseDataTableColumnsOptions<T> = {
  columns: Array<DataTableColumn<T>>;
  attributeColumns?: DataTableAttributeColumns<T>;

  columnOrder?: DataTableColumnRefInput[];
  defaultColumnOrder?: DataTableColumnRefInput[];
  onColumnOrderChange?: (order: DataTableColumnRef[]) => void;

  hiddenColumns?: DataTableColumnRefInput[];
  defaultHiddenColumns?: DataTableColumnRefInput[];
  onHiddenColumnsChange?: (hidden: DataTableColumnRef[]) => void;

  attributeColumnKeys?: string[];
  defaultAttributeColumnKeys?: string[];
  onAttributeColumnKeysChange?: (keys: string[]) => void;
};

const EMPTY: string[] = [];

const encodeInputs = (inputs: DataTableColumnRefInput[] | undefined) => inputs?.map(encodeColumnInput);

function useEncodedColumnState(
  controlled: DataTableColumnRefInput[] | undefined,
  defaultValue: DataTableColumnRefInput[] | undefined,
  onChange: ((refs: DataTableColumnRef[]) => void) | undefined,
) {
  const encodedControlled = useMemo(() => encodeInputs(controlled), [controlled]);
  const encodedDefault = useMemo(() => encodeInputs(defaultValue) ?? EMPTY, [defaultValue]);
  const handleChange = useMemo(
    () => (onChange ? (keys: string[]) => onChange(decodeColumnKeys(keys)) : undefined),
    [onChange],
  );
  const [value, setValue] = useControllableState(encodedControlled, encodedDefault, handleChange);
  return [value, setValue, encodedDefault] as const;
}

export function useDataTableColumns<T>({
  columns,
  attributeColumns,
  columnOrder,
  defaultColumnOrder,
  onColumnOrderChange,
  hiddenColumns,
  defaultHiddenColumns,
  onHiddenColumnsChange,
  attributeColumnKeys,
  defaultAttributeColumnKeys = EMPTY,
  onAttributeColumnKeysChange,
}: UseDataTableColumnsOptions<T>) {
  const [order, setOrder, encodedDefaultOrder] = useEncodedColumnState(
    columnOrder,
    defaultColumnOrder,
    onColumnOrderChange,
  );
  const [hidden, setHidden, encodedDefaultHidden] = useEncodedColumnState(
    hiddenColumns,
    defaultHiddenColumns,
    onHiddenColumnsChange,
  );
  const [selectedKeys, setSelectedKeys] = useControllableState(
    attributeColumnKeys,
    defaultAttributeColumnKeys,
    onAttributeColumnKeysChange,
  );

  const attributeColumnDefs = useMemo<Array<ResolvedColumn<T>>>(() => {
    if (!attributeColumns) {
      return [];
    }

    const {
      renderCell,
      getSortValue,
      getLabel,
      getHeaderActions,
      isRowHeader,
      allowsSorting,
      defaultWidth,
      getDefaultWidth,
      getMinWidth,
      hasDefaultCellActions,
    } = attributeColumns;

    return [...new Set(selectedKeys)].map((key) => ({
      id: attributeColumnKey(key),
      ref: { type: 'attribute', id: key },
      name: getLabel ? getLabel(key) : key,
      renderCell: (row: T) => renderCell(row, key),
      getSortValue: getSortValue ? (row: T) => getSortValue(row, key) : undefined,
      headerActions: getHeaderActions?.(key),
      isRowHeader: isRowHeader?.(key),
      allowsSorting,
      defaultWidth: getDefaultWidth?.(key) ?? defaultWidth,
      minWidth: getMinWidth?.(key),
      hasDefaultCellActions,
      isAttribute: true,
    }));
  }, [attributeColumns, selectedKeys]);

  const allColumns = useMemo<Array<ResolvedColumn<T>>>(
    () => [
      ...columns.map((column) => ({
        ...column,
        id: standardColumnKey(column.id),
        ref: { type: 'standard' as const, id: column.id },
        isAttribute: false,
      })),
      ...attributeColumnDefs,
    ],
    [columns, attributeColumnDefs],
  );

  const orderedColumns = useMemo(() => {
    const byId = new Map(allColumns.map((column) => [column.id, column]));
    const ordered: Array<ResolvedColumn<T>> = [];

    for (const id of order) {
      const column = byId.get(id);
      if (column) {
        ordered.push(column);
        byId.delete(id);
      }
    }

    for (const column of allColumns) {
      if (byId.has(column.id)) {
        ordered.push(column);
      }
    }

    return ordered;
  }, [allColumns, order]);

  const hiddenSet = useMemo(() => new Set(hidden), [hidden]);

  const visibleColumns = useMemo(
    () => orderedColumns.filter((column) => column.isHideable === false || !hiddenSet.has(column.id)),
    [orderedColumns, hiddenSet],
  );

  const toggleColumnVisibility = useCallback(
    (id: string, isVisible: boolean) => {
      if (isVisible) {
        setHidden(hidden.filter((hiddenId) => hiddenId !== id));
      } else if (!hiddenSet.has(id)) {
        setHidden([...hidden, id]);
      }
    },
    [hidden, hiddenSet, setHidden],
  );

  const canMoveColumn = useCallback(
    (id: string, toIndex: number) => {
      const fromIndex = orderedColumns.findIndex((column) => column.id === id);
      if (fromIndex === -1 || toIndex < 0 || toIndex >= orderedColumns.length || fromIndex === toIndex) {
        return false;
      }

      const source = orderedColumns[fromIndex];
      const crossedColumns = orderedColumns.slice(Math.min(fromIndex, toIndex), Math.max(fromIndex, toIndex) + 1);
      return (
        source.isReorderable !== false &&
        crossedColumns.every((column) => column.id === id || column.isReorderable !== false)
      );
    },
    [orderedColumns],
  );

  const moveColumn = useCallback(
    (id: string, toIndex: number) => {
      const ids = orderedColumns.map((column) => column.id);
      const fromIndex = ids.indexOf(id);

      if (!canMoveColumn(id, toIndex)) {
        return;
      }

      ids.splice(fromIndex, 1);
      ids.splice(toIndex, 0, id);
      setOrder(ids);
    },
    [canMoveColumn, orderedColumns, setOrder],
  );

  const canMoveColumnRelative = useCallback(
    (id: string, targetId: string, position: 'before' | 'after' | 'on') => {
      if (position === 'on') {
        return false;
      }
      return moveWithinReorderableSlots(orderedColumns, id, targetId, position) !== undefined;
    },
    [orderedColumns],
  );

  const moveColumnRelative = useCallback(
    (id: string, targetId: string, position: 'before' | 'after' | 'on') => {
      if (position === 'on') {
        return;
      }
      const next = moveWithinReorderableSlots(orderedColumns, id, targetId, position);
      if (!next) {
        return;
      }
      setOrder(next);
    },
    [orderedColumns, setOrder],
  );

  const addAttributeColumn = useCallback(
    (key: string) => {
      if (selectedKeys.includes(key)) {
        return;
      }
      setSelectedKeys([...selectedKeys, key]);
    },
    [selectedKeys, setSelectedKeys],
  );

  const removeAttributeColumn = useCallback(
    (key: string) => {
      const columnKey = attributeColumnKey(key);
      setSelectedKeys(selectedKeys.filter((selected) => selected !== key));
      setOrder(order.filter((id) => id !== columnKey));
      setHidden(hidden.filter((id) => id !== columnKey));
    },
    [selectedKeys, setSelectedKeys, order, setOrder, hidden, setHidden],
  );

  const resetColumns = useCallback(() => {
    setOrder(encodedDefaultOrder);
    setHidden(encodedDefaultHidden);
    setSelectedKeys(defaultAttributeColumnKeys);
  }, [encodedDefaultOrder, encodedDefaultHidden, defaultAttributeColumnKeys, setOrder, setHidden, setSelectedKeys]);

  return {
    orderedColumns,
    visibleColumns,
    hiddenColumns: hiddenSet,
    attributeColumnKeys: selectedKeys,
    toggleColumnVisibility,
    canMoveColumn,
    canMoveColumnRelative,
    moveColumn,
    moveColumnRelative,
    applyColumnOrder: setOrder,
    addAttributeColumn,
    removeAttributeColumn,
    resetColumns,
  };
}
